import { PaymentMethod } from "../../../common/enums";
import { type BankAddress } from "../Components/BankValidators/types";
import { type PaymentPermissionsByArena } from "../Containers/PaymentPrefForPositionContainer/types";
import {
  type AccountEdge,
  type PaymentPrefCurrentUser,
  SecurityTriggerStatus,
} from "../Context/types";
import {
  Address,
  type DetailedPaymentPrefUnionObjectType,
  type RedactedPaymentPref,
} from "./types";

/**
 * Checks if the BankAddressField object has any non-null and non-empty values.
 *
 * @param {BankAddressField} bankAddress - The bank address object to check.
 * @returns {boolean} - Returns true if any field has a value, otherwise false.
 * @throws {TypeError} - If the input is not a BankAddressField object.
 */
export function hasBankInfo(bankAddress?: BankAddress | null): boolean {
  if (typeof bankAddress !== "object" || bankAddress === null || !bankAddress) {
    return false;
  }

  // We don't care if there are values in region field as there are some edge-cases
  // related to the non-required nature of the BankAddressInput fields where a user
  // can wipe out all values causing weird UI issues
  return Object.entries(bankAddress).some(
    ([key, value]) => key !== "region" && typeof value === "string" && value.trim() !== "",
  );
}

export const isOtherPaymentMethod = (
  paymentPref: DetailedPaymentPrefUnionObjectType | RedactedPaymentPref,
): boolean =>
  "paymentMethod" in paymentPref && paymentPref.paymentMethod?.toUpperCase() === "OTHER";

export const getPaymentMethod = (
  paymentPref: DetailedPaymentPrefUnionObjectType | RedactedPaymentPref,
): PaymentMethod => {
  if (!("paymentMethod" in paymentPref)) {
    throw new Error(`Unknown payment method.`);
  }
  // Note that the paymentMethod field is a string, so we convert it to uppercase
  // before comparing it to the enum values to avoid case-sensitivity issues.
  switch (paymentPref.paymentMethod?.toUpperCase()) {
    case PaymentMethod.ACH.toUpperCase():
      return PaymentMethod.ACH;
    case PaymentMethod.Wire.toUpperCase():
      return PaymentMethod.Wire;
    case PaymentMethod.Check.toUpperCase():
      return PaymentMethod.Check;
    case PaymentMethod.BACS.toUpperCase():
      return PaymentMethod.BACS;
    case PaymentMethod.BECS.toUpperCase():
      return PaymentMethod.BECS;
    case PaymentMethod.SEPA.toUpperCase():
      return PaymentMethod.SEPA;
    case PaymentMethod.EFT.toUpperCase():
      return PaymentMethod.EFT;
    case PaymentMethod.Other.toUpperCase():
      return PaymentMethod.Other;
    default:
      throw new Error(`Unknown payment method: ${paymentPref.paymentMethod}`);
  }
};

/**
 * Produces a one-line summary of an address.
 *
 * @param address - The address object to summarize
 * @returns A formatted address string in the format: "street city, state/region postalCode country"
 */
export const getAddressSummaryDisplay = (address: Address): string => {
  // Get state or region (prefer state over region)
  const stateOrRegion = address.state || address.region || "";

  // Build the area string (state/region, postal code, country)
  const areaStr = [stateOrRegion, address.postalCode, address.country]
    .filter((part) => part && part.trim() !== "")
    .join(" ");

  // Build the street part (street + city)
  const streetPart = [address.street, address.city]
    .filter((part) => part && part.trim() !== "")
    .join(" ");

  // Combine street part and area part with a comma
  return `${streetPart}, ${areaStr}`;
};

/**
 * Checks if a specific account requires staff approval for LP-initiated payment
 * preference changes.
 *
 * Approval is required when the current user's security verdict is `TRUE`
 * (server-gated) OR the account's arena requires investor approvals. A `TRUE`
 * verdict always routes, regardless of the arena's payment-permission entry.
 *
 * NB: `UNAVAILABLE` is deliberately NOT treated as requiring approval here — it is
 * a hard block handled by {@link submitGateForCurrentUser}, not a CR route.
 *
 * @param account - The account edge object containing account information with arenaId
 * @param paymentPermissionsByArena - Map of payment permissions keyed by arena ID
 * @param currentUser - Current user's server-gated security verdict
 * @returns true if the account requires staff approval, false otherwise
 */
export const accountRequiresStaffApprovalForLPInitiatedChanges = (
  account: AccountEdge,
  paymentPermissionsByArena: PaymentPermissionsByArena | undefined,
  currentUser: PaymentPrefCurrentUser | undefined,
): boolean => {
  if (currentUser?.hasActiveSecurityTriggers === SecurityTriggerStatus.True) {
    return true;
  }

  if (!paymentPermissionsByArena) {
    return false;
  }

  const arenaId = String(account.node.arenaId);
  const permission = paymentPermissionsByArena[arenaId];
  return permission?.requirePositionPaymentPrefInvestorApprovals ?? false;
};

/**
 * Checks if any account in a list requires staff approval for LP-initiated payment
 * preference changes.
 *
 * @param accounts - Array of account edge objects
 * @param paymentPermissionsByArena - Map of payment permissions keyed by arena ID
 * @param currentUser - Current user's server-gated security signal
 * @returns true if at least one account requires staff approval, false otherwise
 */
export const anyAccountRequiresStaffApprovalForLPInitiatedChanges = (
  accounts: AccountEdge[] | null | undefined,
  paymentPermissionsByArena: PaymentPermissionsByArena | undefined,
  currentUser: PaymentPrefCurrentUser | undefined,
): boolean => {
  if (!accounts) {
    return false;
  }

  return accounts.some((account) =>
    accountRequiresStaffApprovalForLPInitiatedChanges(
      account,
      paymentPermissionsByArena,
      currentUser,
    ),
  );
};

/**
 * The three mutually-exclusive outcomes of the LP payment-pref submit gate.
 */
export type SubmitGate = "DIRECT" | "CHANGE_REQUEST" | "BLOCKED";

/**
 * Top-level LP submit gate. Resolves the server-gated security verdict and the
 * arena approval settings into a single outcome:
 *
 *  - `BLOCKED`        — verdict is `UNAVAILABLE` (vendor lookup failed at login).
 *    Submit must be disabled and a temporary-service banner shown; we never
 *    silently allow a write while security evaluation is unavailable (fail closed).
 *  - `CHANGE_REQUEST` — verdict is `TRUE`, OR any account's arena requires investor
 *    approvals and the LP-initiated approval flag is on.
 *  - `DIRECT`         — otherwise.
 *
 * `UNAVAILABLE` short-circuits regardless of arena settings. The security verdict
 * (`TRUE`/`UNAVAILABLE`) routes regardless of `staffApprovalForLPInitiatedPaymentPref`;
 * that flag only gates the pre-existing arena-approval path.
 */
export const submitGateForCurrentUser = (
  accounts: AccountEdge[] | null | undefined,
  paymentPermissionsByArena: PaymentPermissionsByArena | undefined,
  currentUser: PaymentPrefCurrentUser | undefined,
  staffApprovalForLPInitiatedPaymentPref: boolean,
): SubmitGate => {
  if (currentUser?.hasActiveSecurityTriggers === SecurityTriggerStatus.Unavailable) {
    return "BLOCKED";
  }

  const requiresApproval =
    (anyAccountRequiresStaffApprovalForLPInitiatedChanges(
      accounts,
      paymentPermissionsByArena,
      currentUser,
    ) &&
      staffApprovalForLPInitiatedPaymentPref) ||
    currentUser?.hasActiveSecurityTriggers === SecurityTriggerStatus.True;

  return requiresApproval ? "CHANGE_REQUEST" : "DIRECT";
};

export const validateBankCountryMatch = (
  bankCountryName: string | undefined,
  validatedCountry: string | undefined,
): boolean => {
  if (!bankCountryName || !validatedCountry) {
    return true;
  }
  return bankCountryName.toLowerCase() === validatedCountry.toLowerCase();
};
